import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseNotification = API_JSON & {
  type: string;
  severity: string;
  location: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  dismissable: boolean;
};

const order = { 'order[severity]': 'asc', 'order[startDate]': 'asc' } as const;
const groups = [
  'notification-list',
  'notification-startdate',
  'notification-enddate',
  'enabled',
  'notification-products',
  'notification-customers',
  'notification-areas',
  'notification-roles'
];

/**
 * Fetch notifications that the user has access to
 * @param params can be used to customise/limit the notifications retrieved
 * @returns an array of notifications
 */
export const getNotifications: GetMany<BaseNotification> = async (params) =>
  (
    await ASSIST_API.get('notifications/check', {
      params: requestParams({ order, groups }, params)
    })
  ).data;

/**
 * Hook implementation to fetch notifications
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns notifications matching request
 */
export const useNotifications: AssistQuery.UseMany<BaseNotification> = (params, options) =>
  useQueryAssist(['notifications', 'check', params], () => getNotifications(params), {
    staleTime: STALE_SHORT,
    ...options,
    allowNoFilters: true
  });

/**
 * Fetch notifications that the user has access to
 * @param params can be used to customise/limit the notifications retrieved
 * @returns an array of notifications
 */
export const getNotificationCount = async (params?: RequestParams): Promise<number> =>
  (
    await ASSIST_API.get('notifications/check', {
      params: requestParams({}, params)
    })
  ).data?.meta?.totalItems || 0;

/**
 * Hook implementation to fetch notifications
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns notifications matching request
 */
export const useNotificationCount = (
  params?: RequestParams,
  options?: UseQueryOptions<number>
): UseQueryResult<number> =>
  useQueryAssist(['notifications', 'count', params], () => getNotificationCount(params), {
    staleTime: STALE_SHORT,
    ...options,
    allowNoFilters: true
  });
